import { useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { Box, FormControl, InputLabel, Select, SelectChangeEvent } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCompress, faExpand, faTrashCan } from "@fortawesome/free-solid-svg-icons";
import cn from "classnames";
import { useFullscreen, useTranslate } from "@/hooks";
import { wordDeclination } from "@/helpers";
import { colorPrimary, colorRed } from "@/constants/colors";
import { AlertDialog, CustomSwitch, CustomMenuItem, CustomButton } from "@/ui";
import TableCustomPagination from "../TableCustomPagination/TableCustomPagination";
import { ICustomFooterProps, IFooterAlert } from "./TableFooter.props";
import styles from "./TableFooter.module.scss";

const TableFooter = <T,>({
	table,
	rowSelection = {}, // дефолт
	tableContainerRef,
	isHaveChanges = false, // дефолт
	handleSubmit,
	handleDeleteRows,
	submitMode = false,
	withExpand = true,
	additionalContent,
}: ICustomFooterProps<T>): JSX.Element => {
	const pageSize = table.getState().pagination.pageSize;
	const pageSizeVariants = [10, 20, 50, 100];

	const [cookies, setCookie] = useCookies(["confirmationMode"]);
	const [isConfirmationMode, setisConfirmationMode] = useState<boolean>(cookies.confirmationMode === "true");
	const [showAlertDialog, setShowAlertDialog] = useState(false);
	const [alertVariant, setAlertVariant] = useState<IFooterAlert>({
		title: "dialog_confirmation",
		description: "dialog_approveEntriesQuestion",
	});
	const { isFullscreen, toggleFullscreen } = useFullscreen(tableContainerRef);

	const translate = useTranslate();

	const isEmpty = Object.keys(rowSelection).length === 0;

	const handleChangePageSize = (e: SelectChangeEvent<number>) => {
		table.setPageSize(Number(e.target.value));
	};

	const handleToggleConfirm = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.checked;
		setCookie("confirmationMode", `${value}`);
		setisConfirmationMode(value);
	};

	useEffect(() => {
		const value = cookies.confirmationMode === "true";
		setisConfirmationMode(value);
	}, [cookies.confirmationMode]);

	const handleSubmitWithVariants = () => {
		if (!handleSubmit) return; // если нет обработчика, ничего не делаем
		if (isConfirmationMode) {
			setShowAlertDialog(true);
		} else {
			handleSubmit();
		}
	};

	const toggleDeleteConfirmation = () => {
		if (!handleDeleteRows) return; // если нет обработчика — не показываем диалог
		setAlertVariant({
			title: "dialog_confirmation",
			description: "dialog_approveDeleteEntriesQuestion",
		});
		setShowAlertDialog(true);
	};

	return (
		<div
			className={cn(styles.footer, {
				[styles.footerBoxShadow]:
					tableContainerRef?.current &&
					tableContainerRef.current.scrollHeight - tableContainerRef.current.offsetHeight > Math.ceil(tableContainerRef.current.scrollTop) + 1,
			})}
		>
			{(!isEmpty || isHaveChanges) && (
				<>
					{!isEmpty && (
						<div className={styles.selectedRow}>
							<span>
								{translate("title_selected")}: {Object.keys(rowSelection).length}{" "}
								{wordDeclination(Object.keys(rowSelection).length, [translate("title_row"), translate("title_rows"), translate("title_rows2")])}
							</span>
							{handleDeleteRows && (
								<FontAwesomeIcon icon={faTrashCan} color={colorRed} onClick={toggleDeleteConfirmation} title={translate("button_delete")} style={{ cursor: "pointer" }} />
							)}
						</div>
					)}
				</>
			)}

			<Box sx={{ display: "flex", flexDirection: "row", alignItems: "center", width: "100%", flexWrap: "nowrap" }}>
				{withExpand && <FontAwesomeIcon icon={isFullscreen ? faCompress : faExpand} size={"2x"} className={cn(styles.expand)} onClick={toggleFullscreen} />}

				<Box sx={{ display: "flex", gap: "8px", alignItems: "center", justifyContent: "flex-end", width: "100%", height: "100%" }}>
					{submitMode ? (
						<>
							<CustomSwitch checked={isConfirmationMode} onChange={handleToggleConfirm} size="small" labelRight="checkbox_confirmationOfChanges" singleLabel />

							<form className={styles.formApprove}>
								<CustomButton
									disabled={isEmpty || !handleSubmit} // если нет обработчика, дизейблим
									onClick={handleSubmitWithVariants}
									sx={{ flexShrink: 0 }}
								>
									{translate("button_approve")}
								</CustomButton>
							</form>
						</>
					) : additionalContent ? (
						additionalContent
					) : (
						<></>
						// <Box sx={{ marginLeft: "auto" }} />
					)}
					<div className={styles.pagination}>
						<FormControl sx={{ width: 80, ".MuiInputLabel-root[data-shrink='false']": { top: -8 } }}>
							<InputLabel id="demo-simple-select-label" sx={{ fontSize: 13 }}>
								{translate("select_quantityRows")}
							</InputLabel>
							<Select
								labelId="demo-simple-select-label"
								id="simple-select"
								value={pageSize}
								label={translate("select_quantityRows")}
								onChange={handleChangePageSize}
								style={{ fontSize: 13, height: 36, color: colorPrimary }}
								MenuProps={{
									container: tableContainerRef.current,
								}}
							>
								{pageSizeVariants.map((value) => (
									<CustomMenuItem key={value} value={value}>
										{value}
									</CustomMenuItem>
								))}
							</Select>
						</FormControl>

						<TableCustomPagination table={table} tableContainerRef={tableContainerRef} />
					</div>
				</Box>
			</Box>

			<AlertDialog
				showAlertDialog={showAlertDialog}
				setShowAlertDialog={setShowAlertDialog}
				submitHandler={alertVariant.description === "dialog_approveEntriesQuestion" ? handleSubmit ?? (() => {}) : handleDeleteRows ?? (() => {})}
				title={alertVariant.title}
				description={alertVariant.description}
			/>
		</div>
	);
};

export default TableFooter;
